/* eslint-disable max-len */
import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiRejstrikResp } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';
import PhoneNumberInput from 'components/molecules/PhoneNumberInput';

import checkIsin from 'services/akcionar/emise/checkIsin';
import {
    IDodatekAdmin,
    IDodatekIsins,
    dokumentAdminGet,
    dokumentAdminPut,
    dokumentDodatekIsinPut,
    getRequireAdminData
} from 'services/dokumenty/dodatek';
import {
    IZruseniAdminPrikaz,
    PrikazZmenaAdminGet,
    PrikazZmenaAdminPut,
    PrikazZrusAdminGet,
    PrikazZrusAdminPut
} from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';
import leiRejstrkApi from 'services/lei/leiRejstrik';

import emailValidation from 'utils/validations/emailValidation';
import icoNidValidation from 'utils/validations/icoNidValidation';
import { validateICO } from 'utils/validations/icoValidation';
import pscValidation from 'utils/validations/pscValidation';

import { getCountryText } from '../../Lei/formularPages/PrehledPages/DetailInfoPrehled';
import { isIsinDlt } from '../NovyDokument';

interface IAdmin {
    nextStep: () => void;
    prevStep: () => void;
    zadostID: number;
    dodatekData?: IDodatekIsins;
    prikazyData?: ISmlouvniStrany;
    setDotatekData: React.Dispatch<React.SetStateAction<IDodatekIsins | undefined>>;
    signingDisabled: boolean;
    mode: 'navyseni' | 'prikazzmena' | 'prikazruseni' | 'dokument';
}

const Admin: React.FC<IAdmin> = ({
    nextStep,
    prevStep,
    zadostID,
    dodatekData,
    signingDisabled,
    setDotatekData,
    mode,
    prikazyData
}) => {
    const { language, dokumenty, cisStaty } = useContext(Context);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [spolecnostResp, setSpolecnostResp] = useState<ILeiRejstrikResp>();
    const [adminData, setAdminData] = useState<IDodatekAdmin | undefined>();
    const [adminDataZrus, setAdminDataZrus] = useState<IZruseniAdminPrikaz | undefined>();
    const [requireAdminData, setRequireAdminData] = useState<boolean>(false);
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        ((mode === 'prikazzmena' || mode === 'prikazruseni') &&
            (dokumenty?.dokumentyPrava?.prikazy || [])
                .filter(e => e.typprikazu === (dokumenty?.selectedZadost?.podtyp! as any))[0]
                .emise.map(e => {
                    return {
                        labelcz: e.isin,
                        value: e.isin
                    };
                })) ||
            []
    );
    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    const [lastCalledIdmb, setLastCalledIdmb] = useState<string>();

    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    const getAdminData = (ico?: string) => {
        const callFunc = () => {
            if (ico && validateICO(ico) === 0)
                leiRejstrkApi({
                    ic: ico || form.getFieldValue('idmb'),
                    stat: 'CZ',
                    lang: language,
                    regauthor: 'RA000163'
                })
                    .then(resp => {
                        setSpolecnostResp(resp);
                        form.setFieldsValue({
                            [mode === 'dokument' || mode === 'navyseni' ? 'nazev' : 'admin']: resp.legalname,
                            // eslint-disable-next-line max-len
                            adma: `${resp.adresy[0]?.firstaddressline}, ${resp.adresy[0]?.city}, ${resp.adresy[0]?.postalcode}, ${getCountryText(
                                resp.adresy[0]?.country || '',
                                language
                            )}`
                        });
                    })
                    .finally(() => setLoading(false));
            else {
                setSpolecnostResp(undefined);
                form.setFieldsValue({
                    nazev: undefined,
                    admin: undefined,
                    adma: undefined
                });
            }
            getRequireAdminData(ico || form.getFieldValue('idmb'))
                .then(() => setRequireAdminData(false))
                .catch(() => setRequireAdminData(true))
                .finally(() => setLoading(false));
        };
        if (form.getFieldValue('idmb')) {
            form.validateFields(['idmb']).then(() => {
                setLastCalledIdmb(form.getFieldValue('idmb'));
                setLoading(true);
                callFunc();
            });
        } else if (ico) {
            setLoading(true);
            setLastCalledIdmb(ico);
            callFunc();
        }
    };

    const editingDisabled =
        form.getFieldValue('typzmeny') === 'PONECHAT' || form.getFieldValue('typzmeny') === 'ODSTRANIT';
    //   mode === 'prikazruseni';

    useEffect(() => {
        form.setFieldsValue({
            /*     idmb: prikazyData?.spolecnost.idmb,
            nazev: prikazyData?.spolecnost.nazev, */
            lei: prikazyData?.spolecnost.lei
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prikazyData]);

    useEffect(() => {
        setLoading(true);
        (mode === 'prikazzmena'
            ? PrikazZmenaAdminGet(zadostID)
            : mode === 'prikazruseni'
              ? PrikazZrusAdminGet(zadostID)
              : dokumentAdminGet(zadostID)
        )
            .then(resp => {
                setSelectIsin(resp.isin);
                if (mode === 'prikazruseni') {
                    setAdminDataZrus(resp as IZruseniAdminPrikaz);
                    setAdminData(resp as IDodatekAdmin);
                    setSpolecnostResp({
                        legalname: resp?.nazev || '',
                        adresy: [
                            {
                                firstaddressline: resp?.adma,
                                city: resp?.mema,
                                postalcode: resp?.pscm,
                                country: resp?.kost
                            }
                        ]
                    } as ILeiRejstrikResp);
                    setLastCalledIdmb(resp.idmb);
                } else {
                    const response = resp as IDodatekAdmin;
                    setSpolecnostResp({
                        legalname: response?.admin || response?.nazev || '',
                        adresy: [
                            {
                                firstaddressline: response?.adma,
                                city: response?.mema,
                                postalcode: response?.pscm,
                                country: response?.kost
                            }
                        ]
                    } as ILeiRejstrikResp);
                    setLastCalledIdmb(response.idmb);
                    setAdminData(response);
                    form.setFieldsValue({
                        typaut: response.typaut,
                        typzmeny: response.typzmeny
                    });
                    (response.typzmeny === 'ZMENIT' || response.typzmeny === 'ANO') && getAdminData(response?.idmb);
                }
            })
            .catch(() => setAdminData({} as IDodatekAdmin))
            .finally(() => {
                setLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {adminData && (prikazyData || dodatekData) && (
                <FormWrapper
                    outForm={form}
                    onFinish={values => {
                        const dataToSend = {
                            ...(values as any),
                            [mode === 'dokument' || mode === 'navyseni' || mode === 'prikazruseni' ? 'nazev' : 'admin']:
                                values.nazev ||
                                values.admin ||
                                spolecnostResp?.legalname ||
                                adminData?.admin ||
                                adminData.nazev ||
                                prikazyData?.spolecnost.nazev,
                            adma:
                                values.adma ||
                                spolecnostResp?.adresy[0].firstaddressline ||
                                adminData?.adma ||
                                prikazyData?.spolecnost.adma,
                            mema:
                                values.mema ||
                                spolecnostResp?.adresy[0].city ||
                                adminData?.mema ||
                                prikazyData?.spolecnost.mema,
                            pscm:
                                values.pscm ||
                                spolecnostResp?.adresy[0].postalcode ||
                                adminData?.pscm ||
                                prikazyData?.spolecnost.pscm,
                            kost:
                                values.kost ||
                                spolecnostResp?.adresy[0].country ||
                                adminData?.kost ||
                                prikazyData?.spolecnost.kost,
                            ...(values.datum && {
                                datum: values.datum.format('YYYY-MM-DD')
                            })
                        };
                        delete dataToSend.idmbSpolecnost;
                        delete dataToSend.dicdphSpolecnost;
                        delete dataToSend.nazevSpolecnost;
                        delete dataToSend.leiSpolecnost;
                        if (mode === 'dokument' || mode === 'navyseni') {
                            dokumentAdminPut(
                                zadostID,
                                dataToSend.typzmeny === 'NE'
                                    ? ({
                                          typzmeny: 'NE'
                                      } as IDodatekAdmin)
                                    : dataToSend.typzmeny === 'ODSTRANIT' || dataToSend.typzmeny === 'PONECHAT'
                                      ? ({
                                            typzmeny: dataToSend.typzmeny,
                                            nazev: dataToSend.nazev,
                                            idmb: dataToSend.idmb
                                        } as IDodatekAdmin)
                                      : (dataToSend as IDodatekAdmin)
                            );
                            setLoading(true);
                            dokumentDodatekIsinPut(zadostID, {
                                ...dodatekData!,
                                admin: values.typzmeny === 'ANO'
                            })
                                .then(() => {
                                    setDotatekData({
                                        ...dodatekData!,
                                        admin: values.typzmeny === 'ANO'
                                    });
                                    setLoading(false);
                                    nextStep();
                                })
                                .catch(() => setLoading(false));
                        } else if (mode === 'prikazruseni') {
                            PrikazZrusAdminPut(zadostID, dataToSend)
                                .then(() => {
                                    setLoading(false);
                                    nextStep();
                                })
                                .catch(() => setLoading(false));
                        } else {
                            PrikazZmenaAdminPut(zadostID, dataToSend)
                                .then(() => {
                                    setLoading(false);
                                    nextStep();
                                })
                                .catch(() => setLoading(false));
                        }
                    }}
                >
                    <>
                        {mode === 'dokument' || mode === 'navyseni' ? (
                            <>
                                <Title
                                    level={4}
                                    text="ADMINISTRATOR_EMISE"
                                    style={{
                                        marginTop: 20
                                    }}
                                />
                                <Paragraph
                                    style={{
                                        display: 'block',
                                        position: 'relative',
                                        top: -8
                                    }}
                                    text={'DODATEK_OSOBY_TEXT'}
                                />
                                {!adminData?.idmb || adminData.typzmeny === 'ANO' || adminData.typzmeny === 'NE' ? (
                                    <SelectComponent
                                        name="typzmeny"
                                        data={[
                                            {
                                                labelcz: 'ADMIN_ANO',
                                                value: 'ANO'
                                            },
                                            {
                                                labelcz: 'ADMIN_NE',
                                                value: 'NE'
                                            }
                                        ]}
                                        defaultValue={
                                            adminData?.typzmeny || typeof dodatekData?.admin === 'boolean'
                                                ? dodatekData?.admin
                                                    ? 'ANO'
                                                    : 'NE'
                                                : undefined
                                        }
                                        isFormItem={true}
                                        onChange={() => rerender(pv => !pv)}
                                        disabled={signingDisabled}
                                        labelExtra={mode === 'navyseni' && !adminData?.idmb ? 'DOPLNIT_ADMINA' : ''}
                                    />
                                ) : (
                                    <SelectComponent
                                        name="typzmeny"
                                        data={[
                                            {
                                                labelcz: 'ADMIN_PONEHCAT',
                                                value: 'PONECHAT'
                                            },
                                            {
                                                labelcz: 'ADMIN_ZMENIT',
                                                value: 'ZMENIT'
                                            },
                                            {
                                                labelcz: 'ADMIN_ODSTRANIT',
                                                value: 'ODSTRANIT'
                                            }
                                        ]}
                                        defaultValue={adminData?.typzmeny}
                                        isFormItem={true}
                                        onChange={val => {
                                            if (val === 'PONECHAT' || val === 'ODSTRANIT') {
                                                checkIsin(dodatekData?.isin!).then(resp => {
                                                    const respObj = {
                                                        idmb:
                                                            resp.adic && resp.adic.length === 10
                                                                ? resp.adic.slice(2)
                                                                : resp.adic,
                                                        legalname: resp.adna,
                                                        adresy: [
                                                            {
                                                                adma: resp?.admbadma,
                                                                mema: resp?.admbmema,
                                                                pscm: resp?.admbpscm,
                                                                kost: resp?.admbkost
                                                            }
                                                        ]
                                                    };
                                                    form.setFieldsValue({
                                                        idmb:
                                                            resp.adic && resp.adic.length === 10
                                                                ? resp.adic.slice(2)
                                                                : resp.adic,
                                                        nazev: resp.adna,
                                                        adma: `${resp?.admbadma}, ${resp?.admbmema}, ${resp?.admbpscm}, ${getCountryText(
                                                            resp?.admbkost || '',
                                                            language
                                                        )}`
                                                    });
                                                    setSpolecnostResp(respObj as any);
                                                });
                                            }
                                            rerender(pv => !pv);
                                        }}
                                        disabled={signingDisabled}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Title text="EMITENT" level={5} />
                                <InputComponent
                                    disabled={true}
                                    initialValue={prikazyData?.spolecnost.idmb}
                                    label="IDENTIFIKACNI_CISLO"
                                    name="idmbSpolecnost"
                                    notRequired={true}
                                />
                                {prikazyData?.spolecnost.dicdph && (
                                    <InputComponent
                                        disabled={true}
                                        initialValue={prikazyData?.spolecnost.dicdph}
                                        label="ISIN_IDENTIFIKACE_DIC"
                                        name="dicdphSpolecnost"
                                        notRequired={true}
                                    />
                                )}
                                <InputComponent
                                    disabled={true}
                                    initialValue={prikazyData?.spolecnost.nazev}
                                    label="ISIN_IDENTIFIKACE_FIRMA"
                                    name="nazevSpolecnost"
                                    notRequired={true}
                                />
                                {prikazyData?.spolecnost.lei && (
                                    <InputComponent
                                        disabled={true}
                                        initialValue={prikazyData?.spolecnost.lei}
                                        label="LEI_CELE"
                                        name="leiSpolecnost"
                                        notRequired={true}
                                    />
                                )}
                                <SelectComponent
                                    name="isin"
                                    labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                                    defaultValue={adminData?.isin || adminDataZrus?.isin}
                                    data={dataIsins}
                                    isFormItem={true}
                                    onChange={val => setSelectIsin(val)}
                                />
                                <DatePickerComponent
                                    name="datum"
                                    initialValue={
                                        adminData?.datum || adminDataZrus?.datum
                                            ? dayjs(adminData?.datum || adminDataZrus?.datum)
                                            : undefined
                                    }
                                    disabledDate={current =>
                                        mode === 'prikazzmena' || mode === 'prikazruseni'
                                            ? false
                                            : current < dayjs().add(4, 'days')
                                    }
                                    label="PRIKAZ_PREVOD_DATUM"
                                />
                            </>
                        )}
                        {((form.getFieldValue('typzmeny') && form.getFieldValue('typzmeny') !== 'NE' && adminData) ||
                            mode === 'prikazruseni' ||
                            mode === 'prikazzmena') && (
                            <>
                                <Title level={4} text="DODATEK_OSOBY_SUBTITLE" />
                                <Title level={5} text="DODATEK_OSOBY_SUBTITLE_IDENTIFIKACE" dividerTitle={true} />
                                <Row gutter={12}>
                                    <Col span={21}>
                                        <InputComponent
                                            label="ICO_NID_ZASTUPCE"
                                            name="idmb"
                                            initialValue={adminData?.idmb}
                                            type="text"
                                            rules={
                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                    ? undefined
                                                    : icoNidValidation()
                                            }
                                            maxLength={10}
                                            disabled={signingDisabled || editingDisabled}
                                            notRequired={
                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                            }
                                        />
                                    </Col>
                                    <Col
                                        span={3}
                                        style={{
                                            paddingTop: '35px'
                                        }}
                                    >
                                        <ButtonComponent
                                            id="zpetBtn"
                                            buttontext="VYHLEDAT"
                                            htmlType="button"
                                            onClick={() => getAdminData(form.getFieldValue('idmb'))}
                                            disabled={signingDisabled || editingDisabled}
                                        />
                                    </Col>
                                </Row>
                                <InputComponent
                                    label="NAZEV_ZASTUPCE"
                                    name={mode === 'dokument' || mode === 'navyseni' ? 'nazev' : 'admin'}
                                    type="text"
                                    disabled={
                                        (!!spolecnostResp?.legalname &&
                                            validateICO(form.getFieldValue('idmb')) === 0) ||
                                        !form.getFieldValue('idmb')
                                    }
                                    initialValue={
                                        spolecnostResp?.legalname ||
                                        (mode === 'dokument' || mode === 'navyseni' ? adminData.nazev : adminData.admin)
                                    }
                                    notRequired={
                                        form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                        form.getFieldValue('typzmeny') === 'ODSTRANIT' ||
                                        mode === 'prikazruseni'
                                    }
                                />
                                {(form.getFieldValue('typzmeny') === 'ANO' ||
                                    form.getFieldValue('typzmeny') === 'ZMENIT' ||
                                    mode === 'prikazzmena' ||
                                    mode === 'prikazruseni') && (
                                    <>
                                        {mode !== 'prikazruseni' && (
                                            <>
                                                {validateICO(form.getFieldValue('idmb')) === 0 &&
                                                !!spolecnostResp?.adresy &&
                                                !!spolecnostResp.adresy[0].firstaddressline ? (
                                                    <InputComponent
                                                        label="ADRESA_ZASTUPCE"
                                                        name="adma"
                                                        type="text"
                                                        disabled={
                                                            (!!spolecnostResp?.adresy &&
                                                                !!spolecnostResp.adresy[0].firstaddressline) ||
                                                            !form.getFieldValue('idmb')
                                                        }
                                                        initialValue={
                                                            spolecnostResp?.adresy[0].firstaddressline
                                                                ? // eslint-disable-next-line max-len
                                                                  `${spolecnostResp?.adresy[0].firstaddressline}, ${spolecnostResp?.adresy[0].city}, ${spolecnostResp?.adresy[0].postalcode}, ${getCountryText(
                                                                      spolecnostResp?.adresy[0].country || '',
                                                                      language
                                                                  )}`
                                                                : ''
                                                        }
                                                        notRequired={
                                                            form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                            form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                        }
                                                    />
                                                ) : (
                                                    form.getFieldValue('idmb') && (
                                                        <>
                                                            <InputComponent
                                                                label="ULICE_CP"
                                                                name="adma"
                                                                initialValue={adminData.adma}
                                                            />
                                                            <InputComponent
                                                                label="MESTO"
                                                                name="mema"
                                                                initialValue={adminData.mema}
                                                            />
                                                            <InputComponent
                                                                label="PSC"
                                                                name="pscm"
                                                                initialValue={adminData.pscm}
                                                                usePscFormater={
                                                                    form.getFieldValue('kost') === 'SK' ||
                                                                    form.getFieldValue('kost') === 'CZ'
                                                                }
                                                                rules={pscValidation('kost')}
                                                            />
                                                            <SelectComponent
                                                                name="kost"
                                                                labelParagraph="STAT_SIDLA"
                                                                isFormItem={true}
                                                                data={cisStaty}
                                                                defaultValue={adminData.kost}
                                                            />
                                                        </>
                                                    )
                                                )}
                                                {requireAdminData && (
                                                    <>
                                                        <Title
                                                            level={5}
                                                            text="IDENTIFIKACE_ZASTUPCE"
                                                            dividerTitle={true}
                                                        />
                                                        <InputComponent
                                                            label="FORMULARE_JMENA"
                                                            name="jmma"
                                                            type="text"
                                                            maxLength={200}
                                                            initialValue={adminData?.jmma}
                                                            disabled={signingDisabled || editingDisabled}
                                                            notRequired={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                            }
                                                        />
                                                        <InputComponent
                                                            label="FORMULARE_PRIJMENI"
                                                            name="pjma"
                                                            initialValue={adminData?.pjma}
                                                            type="text"
                                                            maxLength={200}
                                                            disabled={signingDisabled || editingDisabled}
                                                            notRequired={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                            }
                                                        />
                                                        <InputComponent
                                                            label="EMAIL"
                                                            name="email"
                                                            type="text"
                                                            maxLength={200}
                                                            initialValue={adminData?.email}
                                                            rules={emailValidation()}
                                                            disabled={signingDisabled || editingDisabled}
                                                            notRequired={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                            }
                                                        />
                                                        <PhoneNumberInput
                                                            label="TELEFON"
                                                            name="telefon"
                                                            initialValue={adminData?.telefon}
                                                            maxLength={10}
                                                            disabled={signingDisabled || editingDisabled}
                                                            notRequired={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                            }
                                                            rules={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                                    ? []
                                                                    : undefined
                                                            }
                                                        />
                                                        <SelectComponent
                                                            labelParagraph="AUTENTIZACE_ZASTUPCE"
                                                            name="typaut"
                                                            data={[
                                                                {
                                                                    labelcz: 'PXKLIC',
                                                                    value: 'PXKLIC'
                                                                },
                                                                {
                                                                    labelcz: 'CERTIFIKAT',
                                                                    value: 'CERT'
                                                                }
                                                            ]}
                                                            onChange={() => rerender(pv => !pv)}
                                                            defaultValue={adminData?.typaut}
                                                            isFormItem={true}
                                                            disabled={signingDisabled || editingDisabled}
                                                            notRequired={
                                                                form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                            }
                                                        />
                                                        {form.getFieldValue('typaut') === 'CERT' && (
                                                            <>
                                                                <InputComponent
                                                                    label="IDENTIFIKACE_ZASTUPCE_CERT"
                                                                    initialValue={adminData?.cersn}
                                                                    name="cersn"
                                                                    type="text"
                                                                    maxLength={60}
                                                                    disabled={signingDisabled || editingDisabled}
                                                                    notRequired={
                                                                        form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                        form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                                    }
                                                                />
                                                                <InputComponent
                                                                    label="IDENTIFIKACE_ZASTUPCE_CERTISS"
                                                                    name="ceriss"
                                                                    type="text"
                                                                    // disabled={true}
                                                                    initialValue={adminData?.ceriss}
                                                                    disabled={signingDisabled || editingDisabled}
                                                                    notRequired={
                                                                        form.getFieldValue('typzmeny') === 'PONECHAT' ||
                                                                        form.getFieldValue('typzmeny') === 'ODSTRANIT'
                                                                    }
                                                                />
                                                            </>
                                                        )}
                                                        <Paragraph
                                                            style={{
                                                                display: 'block',
                                                                margin: '10px 0px'
                                                            }}
                                                            text={'AUTENTIZACE_ZASTUPCE_TEXT'}
                                                        />
                                                        <Paragraph
                                                            style={{
                                                                display: 'block',
                                                                margin: '10px 0px'
                                                            }}
                                                            text={'AUTENTIZACE_ZASTUPCE_TEXT1'}
                                                        />
                                                        <Paragraph
                                                            style={{
                                                                display: 'block',
                                                                margin: '10px 0px'
                                                            }}
                                                            text={'AUTENTIZACE_ZASTUPCE_TEXT2'}
                                                        />{' '}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        {mode === 'prikazzmena' && <PrilohyTable zadostID={zadostID} required={true} />}
                                        {(mode === 'prikazzmena' || mode === 'prikazruseni') && (
                                            <>
                                                <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />
                                                <Paragraph
                                                    style={{
                                                        display: 'block',
                                                        margin: '10px 0px'
                                                    }}
                                                    text={isDLT ? 'ADMIN_ZMENA_DLT_TEXT1' : 'ADMIN_ZMENA_TEXT1'}
                                                />
                                                <Paragraph
                                                    style={{
                                                        display: 'block',
                                                        margin: '10px 0px'
                                                    }}
                                                    text={isDLT ? 'ADMIN_ZMENA_DLT_TEXT2' : 'ADMIN_ZMENA_TEXT2'}
                                                />
                                                <CheckboxComponent
                                                    name="souhlas"
                                                    checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                                    onChange={() => setIsChecked(pv => !pv)}
                                                    checked={isChecked}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </>
                    <Row className="button-row">
                        {mode !== 'prikazzmena' && mode !== 'prikazruseni' && (
                            <ButtonComponent
                                buttontext="ZPET"
                                onClick={() => prevStep()}
                                id="isinZpetBtn"
                                type="default"
                            />
                        )}
                        <ButtonComponent
                            buttontext="POKRACOVAT"
                            onClick={() => {
                                if (lastCalledIdmb !== form.getFieldValue('idmb')) {
                                    mode === 'prikazzmena' && form.scrollToField('idmb');
                                    getAdminData(form.getFieldValue('idmb'));
                                } else {
                                    form.submit();
                                }
                            }}
                            id="isinPokracovatBtn"
                            disabled={mode === 'prikazzmena' || mode === 'prikazruseni' ? !isChecked : false}
                        />
                    </Row>
                </FormWrapper>
            )}
        </LoadingWrapper>
    );
};

export default Admin;
